import { ConverterSettings } from '@warebee/shared/data-access-layout-import-converter';
import classNames from 'classnames';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
  formatDateTime,
  formatTimeAgoShort,
} from '../../../../common/formatHelper';
import { AsyncLoadStatus } from '../../../../common/types';
import ButtonToggle from '../../../../components/actions/ButtonToggle';
import * as Icon from '../../../../components/icons';
import LoadingIndicator from '../../../../components/LoadingIndicator';
import useSaveConverterBuilder from '../hooks/useSaveConverterBuilder';
import { converterAreaConfiguration } from '../store/converter.area.state';
import {
  converterAllAreasBuilder,
  converterBayTypesBuilder,
  converterBuilderState,
  converterExtraLocations,
  converterLayoutBuilder,
  converterLayoutData,
  converterLiveLayoutUpdate,
  converterLocationPatches,
  converterLocationTypesBuilder,
  converterShelvesSettingsBuilder,
  converterSpacerAreaSettings,
} from '../store/converter.state';

export const ConverterSaveBuilderPanel: React.FC = () => {
  const layoutData = useRecoilValue(converterLayoutData);
  const layoutSettings = useRecoilValue(converterLayoutBuilder);
  const areasConfigurations = useRecoilValue(converterAreaConfiguration);
  const areasBuilder = useRecoilValue(converterAllAreasBuilder);
  const locationsPatches = useRecoilValue(converterLocationPatches);
  const locationTypesBuilder = useRecoilValue(converterLocationTypesBuilder);
  const bayTypes = useRecoilValue(converterBayTypesBuilder);
  const shelvesSettings = useRecoilValue(converterShelvesSettingsBuilder);
  const areaSpacers = useRecoilValue(converterSpacerAreaSettings);
  const extraLocations = useRecoilValue(converterExtraLocations);
  const save = useSaveConverterBuilder();
  const builderState = useRecoilValue(converterBuilderState);
  const [liveLayoutUpdateEnabled, setLiveUpdateEnabled] = useRecoilState(
    converterLiveLayoutUpdate,
  );
  const { t } = useTranslation('designer');

  useEffect(() => {
    if (layoutData?.id) {
      const objectToSave: ConverterSettings = {
        layoutSettings,
        areasConfigurations,
        areasBuilder,
        locationsPatches,
        locationsTypes: locationTypesBuilder,
        bayTypes,
        shelvesSettings,
        areaSpacers,
        extraLocations,
      };
      save(layoutData?.id, objectToSave);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    areasBuilder,
    areasConfigurations,
    layoutData?.id,
    layoutSettings,
    locationTypesBuilder,
    locationsPatches,
    bayTypes,
    shelvesSettings,
    extraLocations,
  ]);

  if (!builderState) {
    return null;
  }

  return builderState.status === AsyncLoadStatus.Loading ? (
    <LoadingIndicator
      className="text-xxs md:text-xs"
      message={t`Updating`}
      inline
      transparent
    />
  ) : (
    <>
      <span
        className={'text-menu-100 hidden text-xs lg:block'}
      >{t`Updated:`}</span>
      <span
        title={formatDateTime(builderState.updatedAt)}
        className={'text-xxs md:text-xs ltr:ml-2 rtl:mr-2'}
      >
        {formatTimeAgoShort(builderState.updatedAt)}
      </span>
      {/* <ButtonSwitchMulti
        autoSize
        className="text-minimal mx-1 truncate xl:mx-3"
        classNameLabel="!text-minimal xl:text-sm py-0.5 px-1"
        buttonType="secondary"
        options={[{ label: t`On` }, { label: t`Off` }]}
        selectedIndex={liveLayoutUpdateEnabled ? 0 : 1}
        onClick={index => setLiveUpdateEnabled(index ? false : true)}
      /> */}

      <ButtonToggle
        className={classNames(
          'group',
          'rounded',
          'flex flex-1 flex-col items-center justify-center',
          'h-6 w-6',
          'bg-menu/30',
          'hover:bg-menu-active hover:text-menu-active-text',
        )}
        onChange={index => setLiveUpdateEnabled(index ? false : true)}
      >
        {liveLayoutUpdateEnabled ? (
          <Icon.Resync className={classNames('h-4 w-4 fill-current p-0.5')} />
        ) : (
          <Icon.Lock className={classNames('h-4 w-4 fill-current p-0.5')} />
        )}
      </ButtonToggle>
    </>
  );
};

export default ConverterSaveBuilderPanel;
